import { initialSequencePoints } from './bpmn-graph.mapper'
import {
  EdgeRect,
  isOrthogonalRoute,
  moveRouteForEndpointRects,
} from './edges/orthogonal-edge.util'

import { Point } from '../../dictionary/geometry.types'

/** Minimal view of a React Flow node while it is being dragged. */
export interface DraggedRectNode {
  id: string
  parentId?: string
  position: Point
}

/** Endpoint rects and route the sequence had when the drag started. */
export interface RouteOrigin {
  points: Point[]
  sourceRect: EdgeRect
  targetRect: EdgeRect
}

export function pointsEqual(a: Point[], b: Point[]): boolean {
  return (
    a.length === b.length &&
    a.every((point, index) => point.x === b[index].x && point.y === b[index].y)
  )
}

/**
 * Slides the route the drag started from onto the new endpoint rects, falling
 * back to a fresh route when the moved result is no longer orthogonal.
 */
export function routeForEndpointRects(
  origin: RouteOrigin,
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): Point[] {
  const points = moveRouteForEndpointRects(
    origin.points,
    origin.sourceRect,
    origin.targetRect,
    sourceRect,
    targetRect
  )

  return isOrthogonalRoute(points) ? points : initialSequencePoints(sourceRect, targetRect)
}

/**
 * Absolute rects of everything the drag moves, from the rects captured at drag
 * start plus the live lane-relative positions: dragged nodes (parents resolved
 * recursively, so a node dragged inside a dragged lane lands right), and the
 * boundary events that travel with a dragged parent by the parent's delta.
 */
export function computeLiveDragRects({
  draggedNodes,
  followingBoundaries,
  originalRects,
  fallbackRect,
}: {
  draggedNodes: DraggedRectNode[]
  /** Boundary-event nodes whose parent is being dragged */
  followingBoundaries: DraggedRectNode[]
  originalRects: Map<string, EdgeRect>
  fallbackRect: (nodeId: string) => EdgeRect | null
}): Map<string, EdgeRect> {
  const draggedById = new Map(draggedNodes.map((node) => [node.id, node]))
  const liveRects = new Map(originalRects)
  const resolveDraggedRect = (node: DraggedRectNode): EdgeRect | null => {
    const original = originalRects.get(node.id)
    if (!original) {
      return null
    }
    if (!node.parentId) {
      return { ...original, x: node.position.x, y: node.position.y }
    }
    const draggedParent = draggedById.get(node.parentId)
    const parentRect = draggedParent
      ? resolveDraggedRect(draggedParent)
      : (originalRects.get(node.parentId) ?? fallbackRect(node.parentId))

    return parentRect
      ? { ...original, x: parentRect.x + node.position.x, y: parentRect.y + node.position.y }
      : null
  }

  draggedNodes.forEach((node) => {
    const rect = resolveDraggedRect(node)
    if (rect) {
      liveRects.set(node.id, rect)
    }
  })
  followingBoundaries.forEach((node) => {
    const originalChild = originalRects.get(node.id)
    const originalParent = originalRects.get(node.parentId)
    const liveParent = liveRects.get(node.parentId)
    if (originalChild && originalParent && liveParent) {
      liveRects.set(node.id, {
        ...originalChild,
        x: originalChild.x + liveParent.x - originalParent.x,
        y: originalChild.y + liveParent.y - originalParent.y,
      })
    }
  })

  return liveRects
}
